import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { mapAsKeys } from '@sama/js-core';
import {
  identity,
  mapValues,
  pickBy,
  pullAt,
  random,
  sample,
  sampleSize,
  times,
} from '@sama/micro-dash';
import { getShapeHelper } from '../../../../../2d/shared/shape-helpers/get-shape-helper';
import { ShapeHelper } from '../../../../../2d/shared/shape-helpers/shape-helper';
import { getKeypointOptionClasses } from '../../../../../2d/shared/tools/keypoint/keypoint-tool';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../core/h3ml/state/task-workspace-store';
import { UndoService } from '../../../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/undo.service';
import { ShapeLocationState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  KeypointShapeState,
  ShapeState2D,
} from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { getEffectiveItem } from '../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { isDynamicOutput } from '../../../../../cross-team-shared/misc-services/tag-helper';
import { ShapeOrGroupOrderItem } from '../../../../../cross-team-shared/state/shape-group-state';
import { ObjectWith } from '../../../../../cross-team-shared/types/misc';
import { getOutputHelper } from '../../../../../shared/output-helpers/get-output-helper';
import {
  Point2D,
  ShapeLocation,
  VectorTaggingShapeType,
} from '../../../../../team-apis/otterdam/answers/shape-typings';
import { KeypointOptionClass } from '../../../../../team-apis/otterdam/keypoint-options';
import { AnnotationOutput } from '../../../../../team-apis/otterdam/outputs/annotation-output';
import { outputAnnotationInfoStoreToken } from '../../annotation-providers';
import { AbstractAnnotationStore2D } from '../../state/root/abstract-annotation-store-2d';

@Component({
  selector: 'h3-add-random-shapes',
  templateUrl: './add-random-shapes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddRandomShapesComponent {
  private readonly outputInfoStateStore = inject(
    outputAnnotationInfoStoreToken,
  );

  types: VectorTaggingShapeType[] = [
    'arrow',
    'cuboid',
    'keypoint_shape',
    'line',
    'point',
    'polygon',
    'rectangle',
    'slider_rectangle',
  ];
  selectedTypes: VectorTaggingShapeType[] = ['rectangle'];
  numShapes = 1;
  maxPoints = 10;
  percentKeyLocations = 10;
  percentMLExtrapolation = 50;

  private sensorRendererStore = this.outputInfoStateStore(
    'sensorRendererState',
  );

  constructor(
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private store: AbstractAnnotationStore2D,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private undoService: UndoService,
  ) {}

  add() {
    // keys of "shapes" should be the index value of the ShapeState2D
    const shapes: ObjectWith<ShapeState2D> = {};
    const orderItems: ShapeOrGroupOrderItem[] = [];
    const nextInternalId =
      this.taskWorkspaceStore('task').state().next_internal_id;
    for (let i = 0; i < this.numShapes; ++i) {
      const shape = this.makeRandomShape(nextInternalId + i);
      shapes[shape.index] = shape;
      orderItems.push({ type: 'shape', index: shape.index });
    }

    this.taskWorkspaceStore('task')('next_internal_id').set(
      nextInternalId + this.numShapes,
    );
    const currentShapeInfo = this.outputInfoStateStore.state();
    const newShapeInfo = {
      shapes: { ...currentShapeInfo.shapes, ...shapes },
      groups: currentShapeInfo.groups,
      shapeAndGroupOrder: [
        ...currentShapeInfo.shapeAndGroupOrder,
        ...orderItems,
      ],
    };
    this.outputInfoStateStore.assign(newShapeInfo);

    this.undoService.pushCurrentState();
  }

  selectRandomSubpoints() {
    const frameNumber = this.store.state().frameNumber;
    const subpointSelectable = new Set([
      'polygon',
      'line',
      'arrow',
      'keypoint_shape',
    ]);
    const selection = mapValues(
      pickBy(this.outputInfoStateStore.state().shapes, (shape) =>
        subpointSelectable.has(shape.type),
      ),
      (shape) => {
        // @ts-ignore -- fix me
        const location = getEffectiveItem(shape.key_locations, frameNumber)!;
        const subpointIndexes = location.points.map((_p, i) => i.toString());
        const selectedIndexes = sampleSize(
          subpointIndexes,
          random(subpointIndexes.length),
        );
        return mapAsKeys(selectedIndexes, (): true => true);
      },
    );
    this.taskWorkspaceStore('shapeSelection').set(
      mapValues(selection, () => true),
    );
  }

  private makeRandomShape(index: number) {
    const type = sample(this.selectedTypes);
    let keypointOptionClass;
    if (type === 'keypoint_shape') {
      keypointOptionClass = sample(
        getKeypointOptionClasses(this.outputInfoStore.state().output),
      );
    }
    let shape = {
      type,
      index,
      key_locations: this.makeRandomLocations(type, keypointOptionClass),
      tags: this.makeRandomStaticTags(),
      dynamic_tags: this.makeRandomDynamicTags(),
    };
    if (keypointOptionClass) {
      // @ts-ignore -- fix me
      shape = {
        ...shape,
        keypointClass: keypointOptionClass.name,
      } as KeypointShapeState;
    }
    return shape;
  }

  private makeRandomLocations(
    type: VectorTaggingShapeType,
    keypointOptionClass?: KeypointOptionClass,
  ) {
    const shapeHelper = getShapeHelper(type);
    const sensorRendererState = this.sensorRendererStore.state();
    const locations = this.pickKeyframes().map(
      (frameNumber): ShapeLocation<Point2D> => ({
        frame_number: frameNumber,
        points: shapeHelper.makeRandomPoints(
          sensorRendererState,
          keypointOptionClass?.points!.length || this.maxPoints,
        ),
        visibility: frameNumber === 0 || Math.random() > 0.5 ? 1 : 0,
      }),
    );
    if (type === 'rectangle') {
      this.extrapolationLocationsForRectangles(locations, shapeHelper);
    }
    return locations;
  }

  private extrapolationLocationsForRectangles(
    locations: ShapeLocationState2D[],
    shapeHelper: ShapeHelper,
  ) {
    const sensorRendererState = this.sensorRendererStore.state();
    const numFrames = sensorRendererState.loadedFrames.length;
    for (let i = 0; i < locations.length; ++i) {
      if (Math.random() < this.percentMLExtrapolation / 100) {
        const endFrame = locations[i + 1]?.frame_number ?? numFrames;
        const location = locations[i];
        const num = Math.min(endFrame - location.frame_number - 1, 10);
        if (num > 0) {
          location.extrapolation = {
            points: times(num, () =>
              shapeHelper.makeRandomPoints(sensorRendererState, 4),
            ),
            source: {
              name: '',
              model_id: '',
            },
          };
        }
      }
    }
  }

  private makeRandomStaticTags() {
    const options = this.outputInfoStore.state().output.answer_options;

    return mapAsKeys(
      this.getNestedOutputNames().filter(
        (name) => !isDynamicOutput(name, options),
      ),
      (name) => this.makeRandomAnswer(name),
    );
  }

  private makeRandomDynamicTags() {
    const options = this.outputInfoStore.state().output.answer_options;

    return mapAsKeys(
      this.getNestedOutputNames().filter((name) =>
        isDynamicOutput(name, options),
      ),
      (name) =>
        this.pickKeyframes().map((frameNumber) => ({
          frame_number: frameNumber,
          value: this.makeRandomAnswer(name),
        })),
    );
  }

  private getNestedOutputNames() {
    return this.outputInfoStore
      .state()
      .output.answer_options.map((answerOption) => answerOption.name);
  }

  private pickKeyframes() {
    const sensorRendererState = this.sensorRendererStore.state();
    const numFrames = sensorRendererState.loadedFrames.length;
    const numKeyframes = Math.ceil(
      (numFrames * this.percentKeyLocations) / 100,
    );

    const keyframes = times(numFrames, identity);
    while (keyframes.length > numKeyframes) {
      pullAt(keyframes, random(keyframes.length));
    }
    return keyframes;
  }

  private makeRandomAnswer(outputName: string) {
    const output =
      this.taskWorkspaceStore.state().outputInfoByName[outputName].output;
    return getOutputHelper(output.type).makeRandomAnswer(output);
  }
}
